import type { NafThemeType } from '@naf/theme';

/**
 * Development-only guard against silent theme-resolution gaps.
 *
 * The NOA-3189 styled-components → Emotion migration means the global base styles and the migrated
 * components resolve colours/fonts from `props.theme.*`. When a theme value is missing, the
 * interpolation resolves to `undefined` and the declaration renders blank — invisible failure. That
 * is exactly how the frontpage link colour regressed to the browser-default blue (`typography.defaultText`
 * silently dropped). These are the paths the base reset + core migrated components depend on; assert
 * them in development so a future theme/provider mismatch fails loudly in the console instead of
 * shipping unstyled UI. Compiled out of production builds via the NODE_ENV guard.
 */
const REQUIRED_THEME_PATHS = [
  'typography.defaultText',
  'typography.textLink',
  'fontFamilies.default',
  'fontFamilies.headers',
  'background.default',
] as const;

const getPath = (obj: unknown, path: string): unknown =>
  path.split('.').reduce<unknown>((acc, key) => (acc == null ? undefined : (acc as Record<string, unknown>)[key]), obj);

// Dedupe by the set of missing paths so a persistent gap logs once, not on every render, while a
// different gap (e.g. another theme variant) still surfaces.
const warnedSignatures = new Set<string>();

export const assertThemeComplete = (theme: NafThemeType | undefined, label = 'theme'): void => {
  if (process.env.NODE_ENV === 'production' || !theme) return;

  const missing = REQUIRED_THEME_PATHS.filter((path) => {
    const value = getPath(theme, path);
    return value == null || value === '';
  });
  if (missing.length === 0) return;

  const signature = missing.join(',');
  if (warnedSignatures.has(signature)) return;
  warnedSignatures.add(signature);

  // eslint-disable-next-line no-console
  console.error(
    `[theme] "${label}" is missing required values: ${missing.join(', ')}.\n` +
      'Migrated (Emotion) global/base styles read these via props.theme; a missing value renders ' +
      'blank CSS — e.g. links fall back to the browser-default blue. Check the theme fed to ' +
      'EmotionThemeProvider / ThemeProvider in ThemedApp.',
  );
};
